import { NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  LineChart,
  BookOpen,
  Bell,
} from 'lucide-react';

interface MobileNavItem {
  key: string;
  href: string;
  icon: typeof LayoutDashboard;
  /** Match this href as a prefix instead of exact (e.g. /journal also matches /journal/trades). */
  prefixMatch?: boolean;
}

const navigation: MobileNavItem[] = [
  { key: 'dashboard', href: '/dashboard', icon: LayoutDashboard },
  { key: 'analysis', href: '/analysis', icon: LineChart, prefixMatch: true },
  { key: 'journal', href: '/journal', icon: BookOpen, prefixMatch: true },
  { key: 'signals', href: '/signals', icon: Bell },
];

export default function MobileNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <nav
      aria-label="Primary"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-bg-secondary/95 backdrop-blur-lg border-t border-border-subtle"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div className="flex items-stretch">
        {navigation.map((item) => {
          const Icon = item.icon;
          const isActive =
            pathname === item.href ||
            (item.prefixMatch && pathname.startsWith(item.href + '/'));

          return (
            <NavLink
              key={item.key}
              to={item.href}
              end={!item.prefixMatch}
              aria-current={isActive ? 'page' : undefined}
              aria-label={t(`nav.${item.key}`)}
              className={`
                relative flex-1 flex flex-col items-center justify-center gap-0.5 py-2 px-1
                min-h-[56px] text-[11px] font-medium transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-primary
                ${
                  isActive
                    ? 'text-accent-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }
              `}
            >
              <Icon className="w-5 h-5" aria-hidden="true" />
              <span className="truncate max-w-full leading-tight">
                {t(`nav.${item.key}`)}
              </span>
              {isActive && (
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full bg-accent-primary"
                />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
